import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MonitorPlay, X } from 'lucide-react';

import { api, ApiError } from '@/lib/api';
import {
  errorKeyFor,
  formatMegabytes,
  playAd,
  type PlayAdResult,
  type ScreensResponse,
} from '@/features/screens/screens';

/**
 * "Play Ad" (owner, 2026-08-21): pick a video from the screens library and it
 * takes over every FREE TV seat now — no tile selection involved. The old
 * shape of this dialog pushed to the SELECTED devices, which on gaming PCs
 * cached a file nothing ever played (the presenter gap) and read as a button
 * that did nothing; the owner's ask is exactly "one press → the unreserved
 * TVs play it", so the targeting moved server-side (`POST /screens/ad`).
 *
 * Seats with someone playing are skipped by the server and reported back by
 * name — an ad never interrupts a paying customer. The permanence tradeoff
 * is unchanged and still stated out loud: the ad BECOMES those seats' idle
 * video until another is assigned.
 */

export function PlayAdDialog({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();

  const [library, setLibrary] = useState<ScreensResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [mediaAssetId, setMediaAssetId] = useState('');
  const [phase, setPhase] = useState<'pick' | 'running' | 'done'>('pick');
  const [result, setResult] = useState<PlayAdResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<ScreensResponse>('/screens');
        if (!cancelled) setLibrary(response);
      } catch {
        if (!cancelled) setLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handlePush(): Promise<void> {
    if (!mediaAssetId) return;
    setPhase('running');
    setFailure(null);
    try {
      setResult(await playAd(mediaAssetId));
    } catch (caught) {
      setFailure(
        caught instanceof ApiError
          ? t(errorKeyFor(caught.code), { defaultValue: caught.message })
          : t('deviceBoard.bulk.failedGeneric'),
      );
    }
    setPhase('done');
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('deviceBoard.playAd.title')}
    >
      <div className="flex max-h-full w-full max-w-md flex-col rounded-card border border-subtle bg-raised shadow-2xl">
        <header className="flex items-center gap-3 border-b border-subtle p-4">
          <MonitorPlay className="h-5 w-5 text-content-secondary" aria-hidden />
          <h2 className="flex-1 truncate text-base font-semibold text-content">
            {t('deviceBoard.playAd.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-control p-1 text-content-secondary hover:bg-hover hover:text-content"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          {phase === 'pick' ? (
            <>
              <p className="text-xs text-content-secondary">{t('deviceBoard.playAd.explain')}</p>

              {/* The accepted tradeoff, stated where the decision is made
                  (R4#6): the ad ASSIGNS the video, so it does not end when
                  the "ad" is over — it IS those seats' idle video until
                  someone assigns another. */}
              <p className="mt-2 text-xs text-content-muted">
                {t('deviceBoard.playAd.replacesIdle')}
              </p>

              <h3 className="mt-4 text-sm font-medium text-content">
                {t('deviceBoard.playAd.chooseMedia')}
              </h3>

              {loadFailed ? (
                <p role="alert" className="mt-2 text-sm text-danger">
                  {t('deviceBoard.playAd.loadFailed')}
                </p>
              ) : library === null ? (
                <p className="mt-2 text-sm text-content-muted">{t('common.loading')}</p>
              ) : library.media.length === 0 ? (
                <p className="mt-2 text-sm text-content-muted">{t('deviceBoard.playAd.noMedia')}</p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {library.media.map((asset) => (
                    <li key={asset.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-control border border-subtle bg-input px-3 py-2 text-sm has-[:checked]:border-primary">
                        <input
                          type="radio"
                          name="playAdMedia"
                          value={asset.id}
                          checked={mediaAssetId === asset.id}
                          onChange={() => setMediaAssetId(asset.id)}
                          className="h-4 w-4 accent-[var(--brand-primary)]"
                        />
                        <span className="min-w-0 flex-1 truncate text-content">
                          {asset.fileName}
                        </span>
                        <span className="tabular shrink-0 text-xs text-content-muted">
                          {formatMegabytes(asset.sizeBytes, i18n.language)}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : phase === 'running' ? (
            <p className="text-sm text-content-muted">{t('deviceBoard.playAd.pushing')}</p>
          ) : failure ? (
            <p role="alert" className="text-sm text-danger">
              {failure}
            </p>
          ) : result ? (
            <div data-play-ad-done className="space-y-2 text-sm">
              {result.playedOn.length > 0 ? (
                <p className="text-content-secondary">
                  {t('deviceBoard.playAd.playedOn', { devices: result.playedOn.join(', ') })}
                </p>
              ) : (
                <p className="text-content-secondary">{t('deviceBoard.playAd.noneFree')}</p>
              )}
              {result.inSession.length > 0 ? (
                <p className="text-xs text-content-muted">
                  {t('deviceBoard.playAd.skippedInSession', {
                    devices: result.inSession.join(', '),
                  })}
                </p>
              ) : null}
              {result.playedOn.length > 0 && !result.pushed ? (
                <p className="rounded-control bg-ending/15 px-3 py-2 text-xs text-content">
                  {t('deviceBoard.playAd.bridgeOffline')}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <footer className="flex justify-end gap-2 border-t border-subtle p-4">
          {phase === 'pick' ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handlePush()}
                disabled={!mediaAssetId}
                className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('deviceBoard.playAd.push')}
              </button>
            </>
          ) : phase === 'done' ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
            >
              {t('deviceBoard.bulk.done')}
            </button>
          ) : null}
        </footer>
      </div>
    </div>
  );
}
